// DS EDIT 12 — CMS-authored first-signup tour (ds_onboarding_slides, managed
// in the Admin Panel plugin). Swipeable, always skippable; renders nothing
// when no live slides exist so the classic wizard flow is untouched.
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";

interface TourSlide {
  id: string;
  sort_order: number;
  title: string;
  body: string;
  image_url: string | null;
}

export function useOnboardingTourSlides() {
  return useQuery<TourSlide[]>({
    queryKey: ["ds-onboarding-slides", "live"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ds_onboarding_slides")
        .select("id, sort_order, title, body, image_url")
        .eq("review_status", "live")
        .order("sort_order", { ascending: true });
      if (error) return []; // table absent / RLS: tour simply doesn't render
      return (data ?? []) as TourSlide[];
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function OnboardingTourSlides({
  slides,
  onDone,
}: {
  slides: TourSlide[];
  onDone: () => void;
}) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const slide = slides[index];
  if (!slide) return null;

  const next = () => (index >= slides.length - 1 ? onDone() : setIndex(index + 1));
  const back = () => setIndex(Math.max(0, index - 1));

  return (
    <div
      className="flex flex-col items-center gap-4 py-6 px-4 text-center select-none"
      data-testid="onboarding-tour"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = touchStartX.current;
        touchStartX.current = null;
        if (start == null) return;
        const delta = (e.changedTouches[0]?.clientX ?? start) - start;
        if (delta < -40) next(); // swipe left → forward
        if (delta > 40) back(); // swipe right → back
      }}
    >
      {slide.image_url && (
        <img
          src={slide.image_url}
          alt=""
          className="max-h-40 rounded-lg object-contain"
          loading="lazy"
        />
      )}
      <h2 className="text-xl font-semibold" data-testid="tour-slide-title">
        {slide.title}
      </h2>
      <p className="text-muted-foreground max-w-md whitespace-pre-line">{slide.body}</p>

      <div className="flex items-center gap-1.5 pt-1" aria-hidden>
        {slides.map((s, i) => (
          <span
            key={s.id}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>

      <div className="flex w-full items-center justify-between pt-2">
        <Button variant="ghost" onClick={onDone} data-testid="button-skip-tour">
          Skip tour
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={back} disabled={index === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button onClick={next} data-testid="button-tour-next">
            {index >= slides.length - 1 ? "Get started" : "Next"}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
